import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { GoalListRecord, QueuedMutation } from "../../shared/domain";

export type CachedList = GoalListRecord & {
  cacheKey: string;
  cachedAt: string;
  provisional?: boolean;
  awaitingReconciliation?: boolean;
};

export type LocalConflict = {
  id: string;
  userId: string;
  operation: QueuedMutation;
  authoritative: GoalListRecord | null;
  local: CachedList | null;
  createdAt: string;
};

export type LocalRecovery = {
  id: string;
  userId: string;
  sourceListId: string;
  logicalPeriodKey: string;
  operations: QueuedMutation[];
  createdAt: string;
  serverDraftId?: string;
};

interface TenThingzDB extends DBSchema {
  lists: {
    key: string;
    value: CachedList;
    indexes: { "by-user": string; "by-user-horizon": [string, string] };
  };
  queue: {
    key: string;
    value: QueuedMutation;
    indexes: {
      "by-user-sequence": [string, number];
      "by-user-list": [string, string];
    };
  };
  conflicts: {
    key: string;
    value: LocalConflict;
    indexes: { "by-user": string };
  };
  recovery: {
    key: string;
    value: LocalRecovery;
    indexes: { "by-user": string };
  };
  meta: {
    key: string;
    value: { key: string; userId: string; value: unknown; updatedAt: string };
    indexes: { "by-user": string };
  };
  device: {
    key: string;
    value: {
      key: string;
      userId: string;
      name: string;
      sessionExpiresAt: string;
      updatedAt: string;
    };
  };
}

let databasePromise: Promise<IDBPDatabase<TenThingzDB>> | undefined;

export function offlineDatabase() {
  databasePromise ??= openDB<TenThingzDB>("10thingz-offline", 4, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const lists = db.createObjectStore("lists", { keyPath: "cacheKey" });
        lists.createIndex("by-user", "ownerId");
        lists.createIndex("by-user-horizon", ["ownerId", "horizon"]);
        const queue = db.createObjectStore("queue", { keyPath: "operationId" });
        queue.createIndex("by-user-sequence", ["userId", "sequence"]);
        queue.createIndex("by-user-list", ["userId", "listId"]);
        const meta = db.createObjectStore("meta", { keyPath: "key" });
        meta.createIndex("by-user", "userId");
      }
      if (oldVersion < 2) {
        const conflicts = db.createObjectStore("conflicts", { keyPath: "id" });
        conflicts.createIndex("by-user", "userId");
      }
      if (oldVersion < 3) {
        const recovery = db.createObjectStore("recovery", { keyPath: "id" });
        recovery.createIndex("by-user", "userId");
      }
      if (oldVersion < 4) db.createObjectStore("device", { keyPath: "key" });
    },
    blocking() {
      databasePromise = undefined;
    },
    terminated() {
      databasePromise = undefined;
    },
  });
  return databasePromise;
}

export async function rememberOfflineIdentity(
  userId: string,
  name: string,
  sessionExpiresAt: string,
) {
  const db = await offlineDatabase();
  await db.put("device", {
    key: "last-authenticated-user",
    userId,
    name,
    sessionExpiresAt,
    updatedAt: new Date().toISOString(),
  });
}

export async function offlineIdentity() {
  const db = await offlineDatabase();
  const identity = await db.get("device", "last-authenticated-user");
  if (!identity || Date.parse(identity.sessionExpiresAt) <= Date.now())
    return undefined;
  return identity;
}

export async function forgetOfflineIdentity(userId: string) {
  const db = await offlineDatabase();
  const identity = await db.get("device", "last-authenticated-user");
  if (identity?.userId === userId)
    await db.delete("device", "last-authenticated-user");
}

export async function cacheList(
  userId: string,
  list: GoalListRecord,
  flags: Partial<
    Pick<CachedList, "provisional" | "awaitingReconciliation">
  > = {},
) {
  if (list.ownerId !== userId)
    throw new Error("Cannot cache another account's list.");
  const db = await offlineDatabase();
  await db.put("lists", {
    ...list,
    ...flags,
    cacheKey: `${userId}:${list.id}`,
    cachedAt: new Date().toISOString(),
  });
}

export async function cachedLists(userId: string) {
  const db = await offlineDatabase();
  return db.getAllFromIndex("lists", "by-user", userId);
}

export async function cachedList(userId: string, listId: string) {
  const db = await offlineDatabase();
  const value = await db.get("lists", `${userId}:${listId}`);
  return value?.ownerId === userId ? value : undefined;
}

export async function replaceCachedList(userId: string, list: GoalListRecord) {
  await cacheList(userId, list);
}

export async function reconcileProvisionalList(
  userId: string,
  provisional: CachedList,
  authoritative: GoalListRecord,
) {
  const db = await offlineDatabase();
  const tx = db.transaction(["lists", "queue"], "readwrite");
  const operations = await tx
    .objectStore("queue")
    .index("by-user-list")
    .getAll([userId, provisional.id]);
  let version = authoritative.optimisticVersion;
  for (const operation of operations.sort((a, b) => a.sequence - b.sequence)) {
    await tx.objectStore("queue").put({
      ...operation,
      listId: authoritative.id,
      logicalPeriodKey: authoritative.logicalPeriodKey,
      baseVersion: version,
    });
    version += 1;
  }
  await tx.objectStore("lists").delete(provisional.cacheKey);
  await tx.done;
  return operations;
}

export async function enqueue(operation: QueuedMutation) {
  const db = await offlineDatabase();
  await db.put("queue", operation);
}

export async function queuedForUser(userId: string) {
  const db = await offlineDatabase();
  const range = IDBKeyRange.bound(
    [userId, 0],
    [userId, Number.MAX_SAFE_INTEGER],
  );
  return db.getAllFromIndex("queue", "by-user-sequence", range);
}

export async function removeQueued(userId: string, operationId: string) {
  const db = await offlineDatabase();
  const current = await db.get("queue", operationId);
  if (current?.userId === userId) await db.delete("queue", operationId);
}

export async function updateQueuedRetry(
  userId: string,
  operationId: string,
  lastError: string,
) {
  const db = await offlineDatabase();
  const operation = await db.get("queue", operationId);
  if (!operation || operation.userId !== userId) return;
  const attempts = operation.retry.attempts + 1;
  const delay = Math.min(60_000, 1_000 * 2 ** Math.min(attempts, 6));
  await db.put("queue", {
    ...operation,
    retry: {
      attempts,
      lastError: lastError.slice(0, 300),
      nextAttemptAt: new Date(Date.now() + delay).toISOString(),
    },
  });
}

export async function saveConflict(conflict: LocalConflict) {
  const db = await offlineDatabase();
  await db.put("conflicts", conflict);
}

export async function conflictsForUser(userId: string) {
  const db = await offlineDatabase();
  return db.getAllFromIndex("conflicts", "by-user", userId);
}

export async function removeConflict(userId: string, id: string) {
  const db = await offlineDatabase();
  const conflict = await db.get("conflicts", id);
  if (conflict?.userId === userId) await db.delete("conflicts", id);
}

export async function saveRecovery(recovery: LocalRecovery) {
  const db = await offlineDatabase();
  await db.put("recovery", recovery);
}

export async function recoveriesForUser(userId: string) {
  const db = await offlineDatabase();
  return db.getAllFromIndex("recovery", "by-user", userId);
}

export async function removeRecovery(userId: string, id: string) {
  const db = await offlineDatabase();
  const recovery = await db.get("recovery", id);
  if (recovery?.userId === userId) await db.delete("recovery", id);
}

export async function setMeta(userId: string, name: string, value: unknown) {
  const db = await offlineDatabase();
  await db.put("meta", {
    key: `${userId}:${name}`,
    userId,
    value,
    updatedAt: new Date().toISOString(),
  });
}

export async function getMeta<T>(
  userId: string,
  name: string,
): Promise<{ value: T; updatedAt: string } | undefined> {
  const db = await offlineDatabase();
  const record = await db.get("meta", `${userId}:${name}`);
  if (!record || record.userId !== userId) return undefined;
  return { value: record.value as T, updatedAt: record.updatedAt };
}

export async function clearOfflineData(userId: string) {
  const db = await offlineDatabase();
  const tx = db.transaction(
    ["lists", "queue", "conflicts", "recovery", "meta"],
    "readwrite",
  );
  const deleteIndex = async (
    storeName: "lists" | "conflicts" | "recovery" | "meta",
  ) => {
    let cursor = await tx
      .objectStore(storeName)
      .index("by-user")
      .openKeyCursor(userId);
    while (cursor) {
      await tx.objectStore(storeName).delete(cursor.primaryKey);
      cursor = await cursor.continue();
    }
  };
  await Promise.all([
    deleteIndex("lists"),
    deleteIndex("conflicts"),
    deleteIndex("recovery"),
    deleteIndex("meta"),
  ]);
  const range = IDBKeyRange.bound(
    [userId, 0],
    [userId, Number.MAX_SAFE_INTEGER],
  );
  let queueCursor = await tx
    .objectStore("queue")
    .index("by-user-sequence")
    .openKeyCursor(range);
  while (queueCursor) {
    await tx.objectStore("queue").delete(queueCursor.primaryKey);
    queueCursor = await queueCursor.continue();
  }
  await tx.done;
}

export async function offlineCounts(userId: string) {
  const [queue, conflicts, recovery, lists] = await Promise.all([
    queuedForUser(userId),
    conflictsForUser(userId),
    recoveriesForUser(userId),
    cachedLists(userId),
  ]);
  return {
    queue: queue.length,
    conflicts: conflicts.length,
    recovery: recovery.length,
    lists: lists.length,
  };
}
